import createAuth0Client from '@auth0/auth0-spa-js';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';

const DEFAULT_REDIRECT_CALLBACK = () => {
  let path = window.location.pathname;
  let redirectUrl = null;
  try {
    const url = window.sessionStorage.getItem('redirectUrl');
    if (url !== null) {
      const value = JSON.parse(url);
      const expirationDate = new Date(value.expirationDate);
      if (expirationDate > new Date()) {
        redirectUrl = value.value;
      }
      window.sessionStorage.removeItem('redirectUrl');
    }
    path = redirectUrl || window.location.pathname;
  } catch (error) {
    console.log(error);
  }
  return window.history.replaceState({}, document.title, path);
};

export const Auth0Context = React.createContext();
export const useAuth0 = () => useContext(Auth0Context);

const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [auth0Client, setAuth0] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (window.location.search.includes('tenantId')) {
      try {
        const expirationInMin = 1;
        const expirationDate = new Date(
          new Date().getTime() + 60000 * expirationInMin
        );
        const newValue = {
          value: window.location.href,
          expirationDate: expirationDate.toISOString(),
        };
        window.sessionStorage.setItem('redirectUrl', JSON.stringify(newValue));
      } catch (error) {
        console.log(error);
      }
    }

    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(initOptions);
      setAuth0(auth0FromHook);

      if (
        window.location.search.includes('code=') &&
        window.location.search.includes('state=')
      ) {
        const { appState } = await auth0FromHook.handleRedirectCallback();
        onRedirectCallback(appState);
      }

      const isAuthenticatedAuth0 = await auth0FromHook.isAuthenticated();

      setIsAuthenticated(isAuthenticatedAuth0);

      if (isAuthenticatedAuth0) {
        const userAuth0 = await auth0FromHook.getUser();
        setUser(userAuth0);
      }

      setLoading(false);
    };
    initAuth0();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client.handleRedirectCallback();
    const currentUser = await auth0Client.getUser();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(currentUser);
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        handleRedirectCallback,
        getIdTokenClaims: (...p) => auth0Client.getIdTokenClaims(...p),
        loginWithRedirect: (...p) => auth0Client.loginWithRedirect(...p),
        getTokenSilently: (...p) => auth0Client.getTokenSilently(...p),
        getTokenWithPopup: (...p) => auth0Client.getTokenWithPopup(...p),
        logout: (...p) => auth0Client.logout(...p),
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};

Auth0Provider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  onRedirectCallback: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

export { Auth0Provider };
